import { parsePhone } from '../../core'
import {
  vBoolean,
  vCountry,
  vEmailOrEmpty,
  vMetadata,
  vMilitaryTime,
  vObject,
  vOptional,
  type vOutput,
  vPhoneExt,
  vPicklist,
  vPipe,
  vPostalCode,
  vString,
  vTransform,
} from '../../validation'
import { CountryMapper } from '../country'
import { customsBrokerTypes } from '../customs-broker'
import { flatbedHandlingTypes } from '../location-accessorial'
import { vShipType } from '../ship-type.ts'
import { states } from '../state'

const parseBool = (s: string) => !!s?.match(/x|yes|true|y/i)

export type AddressUploadMeta = {
  title: string
  csvMatch: RegExp
  minWidth?: number
  parse?: (s: string) => any
}

export const addressUploadSchema = vObject({
  company: vPipe(
    vString(),
    vMetadata({ title: 'Company *', csvMatch: /company/i, minWidth: 200 }),
  ),
  address: vPipe(
    vOptional(vString()),
    vMetadata({
      title: 'Address *',
      csvMatch: /^address(?!\s*(2|type))/i,
      minWidth: 200,
    }),
  ),
  address2: vPipe(
    vOptional(vString()),
    vMetadata({ title: 'Address 2', csvMatch: /address\s?2/i, minWidth: 120 }),
  ),
  city: vPipe(
    vOptional(vString()),
    vMetadata({ title: 'City', csvMatch: /city/i, minWidth: 150 }),
  ),
  state: vPipe(
    vPicklist(states.map(s => s.abbreviation)),
    vMetadata({
      title: 'State *',
      csvMatch: /state/i,
      minWidth: 100,
      parse: (s: string) =>
        states.find(st => st.abbreviation === s || st.name === s)
          ?.abbreviation ?? '',
    }),
  ),
  postalCode: vPipe(
    vOptional(vPostalCode()),
    vMetadata({ title: 'Zip *', csvMatch: /zip|postal/i }),
  ),
  country: vPipe(
    vOptional(vCountry),
    vMetadata({
      title: 'Country',
      csvMatch: /country/i,
      parse: CountryMapper.legacyToCore,
    }),
  ),
  contactEmail: vPipe(
    vOptional(vEmailOrEmpty),
    vMetadata({ title: 'Contact Email', minWidth: 200, csvMatch: /email/i }),
  ),
  contactPhone: vPipe(
    vOptional(vString()),
    vTransform(parsePhone),
    vMetadata({
      title: 'Contact Phone',
      minWidth: 140,
      csvMatch: /^(?!.*ext).*phone/i,
    }),
  ),
  contactPhoneExt: vPipe(
    vOptional(vPhoneExt()),
    vMetadata({ title: 'Contact Phone Ext', csvMatch: /ext/i }),
  ),
  contactName: vPipe(
    vOptional(vString()),
    vMetadata({ title: 'Contact Name', minWidth: 200, csvMatch: /name/i }),
  ),
  bolFollowers: vPipe(
    vOptional(vString()),
    vMetadata({
      title: 'Share shipments with',
      minWidth: 200,
      csvMatch: /share/i,
    }),
  ),
  shipType: vPipe(
    vOptional(vShipType()),
    vMetadata({
      title: 'Location Type *',
      minWidth: 200,
      csvMatch: /loc.+type/i,
    }),
  ),
  dockHoursStart: vPipe(
    vOptional(vMilitaryTime()),
    vMetadata({
      title: 'Dock Hours Start',
      minWidth: 130,
      csvMatch: /dock.*start/i,
    }),
  ),
  dockHoursEnd: vPipe(
    vOptional(vMilitaryTime()),
    vMetadata({
      title: 'Dock Hours End',
      minWidth: 130,
      csvMatch: /dock.*end/i,
    }),
  ),
  specialInstructions: vPipe(
    vOptional(vString()),
    vMetadata({
      title: 'Special instructions',
      minWidth: 250,
      csvMatch: /special/i,
    }),
  ),
  internalNotes: vPipe(
    vOptional(vString()),
    vMetadata({
      title: 'Internal notes',
      minWidth: 250,
      csvMatch: /internal\s?notes/i,
    }),
  ),
  adultSignatureRequired: vPipe(
    vOptional(vBoolean()),
    vMetadata({
      title: 'Adult Signature Required',
      minWidth: 175,
      csvMatch: /adult/i,
      parse: parseBool,
    }),
  ),
  callNotifyBeforeDelivery: vPipe(
    vOptional(vBoolean()),
    vMetadata({
      title: 'Call/Notify Before Delivery',
      minWidth: 175,
      csvMatch: /notify/i,
      parse: parseBool,
    }),
  ),
  deliveryAppointment: vPipe(
    vOptional(vBoolean()),
    vMetadata({
      title: 'Delivery Appointment Required',
      minWidth: 200,
      csvMatch: /appointment/i,
      parse: parseBool,
    }),
  ),
  insideDelivery: vPipe(
    vOptional(vBoolean()),
    vMetadata({
      title: 'Inside Delivery',
      csvMatch: /inside.*delivery/i,
      parse: parseBool,
    }),
  ),
  insidePickup: vPipe(
    vOptional(vBoolean()),
    vMetadata({
      title: 'Inside Pickup',
      csvMatch: /inside.*pickup/i,
      parse: parseBool,
    }),
  ),
  liftgateDelivery: vPipe(
    vOptional(vBoolean()),
    vMetadata({
      title: 'Liftgate Delivery',
      csvMatch: /liftgate.*delivery/i,
      parse: parseBool,
    }),
  ),
  liftgatePickup: vPipe(
    vOptional(vBoolean()),
    vMetadata({
      title: 'Liftgate Pickup',
      csvMatch: /liftgate.*pickup/i,
      parse: parseBool,
    }),
  ),
  noSignatureRequired: vPipe(
    vOptional(vBoolean()),
    vMetadata({
      title: 'No Signature Required',
      minWidth: 175,
      csvMatch: /(?=.*no)(?=.*signature)/i,
      parse: parseBool,
    }),
  ),
  signatureRequired: vPipe(
    vOptional(vBoolean()),
    vMetadata({
      title: 'Signature Required',
      minWidth: 165,
      csvMatch: /^(?!.*(no|adult|indirect)).*signature/i,
      parse: parseBool,
    }),
  ),
  indirectSignature: vPipe(
    vOptional(vBoolean()),
    vMetadata({
      title: 'Indirect Signature Required',
      minWidth: 175,
      csvMatch: /indirect/i,
      parse: parseBool,
    }),
  ),
  sortAndSegregate: vPipe(
    vOptional(vBoolean()),
    vMetadata({
      title: 'Sort and Segregate',
      minWidth: 150,
      csvMatch: /sort/i,
      parse: parseBool,
    }),
  ),
  twic: vPipe(
    vOptional(vBoolean()),
    vMetadata({
      title: 'TWIC Required',
      minWidth: 150,
      csvMatch: /twic/i,
      parse: parseBool,
    }),
  ),
  flatbedHandlingType: vPipe(
    vOptional(vPicklist(['', ...flatbedHandlingTypes])),
    vMetadata({
      title: 'Flatbed Handling Type',
      minWidth: 180,
      csvMatch: /flatbed/i,
    }),
  ),
  brokerType: vPipe(
    vOptional(vPicklist(['', ...customsBrokerTypes])),
    vMetadata({ title: 'Broker Type', csvMatch: /broker\s?type/i }),
  ),
  sendBol: vPipe(
    vOptional(vBoolean()),
    vMetadata({ title: 'Send BOL', csvMatch: /send/i, parse: parseBool }),
  ),
})

export type AddressUpload = vOutput<typeof addressUploadSchema>
